import React, { useEffect, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes } from 'react-icons/fa';

interface ModalContextType {
  onHide: () => void;
}

const ModalContext = createContext<ModalContextType | null>(null);

interface ModalProps {
  show: boolean;
  onHide: () => void;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  centered?: boolean;
  fullscreen?: boolean;
  className?: string;
  backdrop?: 'static' | boolean;
  children: React.ReactNode;
}

interface ModalHeaderProps {
  closeButton?: boolean;
  className?: string;
  children: React.ReactNode;
}

interface ModalTitleProps {
  children: React.ReactNode;
}

interface ModalBodyProps {
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}

interface ModalFooterProps {
  children: React.ReactNode;
  className?: string;
}

const Modal: React.FC<ModalProps> & {
  Header: React.FC<ModalHeaderProps>;
  Title: React.FC<ModalTitleProps>;
  Body: React.FC<ModalBodyProps>;
  Footer: React.FC<ModalFooterProps>;
} = ({ show, onHide, size = 'md', centered = false, fullscreen = false, className = '', backdrop = true, children }) => {
  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [show]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onHide();
      }
    };

    if (show) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [show, onHide]);

  if (!show) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-4xl',
    xl: 'max-w-6xl'
  };

  const modalSizeClass = fullscreen ? 'w-full h-full max-w-none max-h-none m-0' : `w-full ${sizeClasses[size]}`;
  const containerClass = fullscreen ? 'p-0' : 'p-4';
  const shouldCloseOnBackdrop = backdrop !== 'static' && backdrop !== false;

  const modalContent = (
    <div className="fixed inset-0 z-[1000] overflow-y-auto">
      <div className={`flex min-h-full items-center justify-center ${containerClass} ${centered ? 'items-center' : 'items-start pt-16'}`}>
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={shouldCloseOnBackdrop ? onHide : undefined}
        />
        
        {/* Modal */}
        <div className={`relative bg-white ${fullscreen ? 'h-full' : 'rounded-lg'} shadow-xl ${modalSizeClass} transform transition-all ${className}`}>
          <ModalContext.Provider value={{ onHide }}>
            {children}
          </ModalContext.Provider>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

const ModalHeader: React.FC<ModalHeaderProps> = ({ closeButton = false, className = '', children }) => {
  const modalContext = useContext(ModalContext);
  
  return (
    <div className={`flex items-center justify-between p-4 border-b border-gray-200 ${className}`}>
      <div className="flex-1">
        {children}
      </div>
      {closeButton && modalContext && (
        <button
          type="button"
          className="ml-4 text-gray-400 hover:text-gray-600 transition-colors"
          onClick={modalContext.onHide}
        >
          <FaTimes size={20} />
        </button>
      )}
    </div>
  );
};

const ModalTitle: React.FC<ModalTitleProps> = ({ children }) => {
  return (
    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
      {children}
    </h3>
  );
};

const ModalBody: React.FC<ModalBodyProps> = ({ className = '', style, children }) => {
  return (
    <div className={`p-4 ${className}`} style={style}>
      {children}
    </div>
  );
};

const ModalFooter: React.FC<ModalFooterProps> = ({ children, className = '' }) => {
  return (
    <div className={`flex items-center justify-end gap-3 p-4 border-t border-gray-200 rounded-b-lg flex-wrap ${className}`}>
      {children}
    </div>
  );
};

Modal.Header = ModalHeader;
Modal.Title = ModalTitle;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;

export default Modal;